import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  ListChecks,
  Bug,
  BookOpen,
  CheckSquare,
} from "lucide-react";
import {
  useUpdateBacklogItem,
  useDeleteBacklogItem,
  type ProjectBacklogItem,
  type ProjectEpic,
  type ProjectSprint,
} from "@/hooks/useProjectPlanning";
import { BacklogItemDialog } from "./BacklogItemDialog";
import { PRIORITY_TONE, TONE_TEXT_CLASS } from "@/lib/statusTone";

const typeIcons = {
  task: <CheckSquare className="h-3.5 w-3.5" />,
  bug: <Bug className="h-3.5 w-3.5" />,
  story: <BookOpen className="h-3.5 w-3.5" />,
};
const priorityColors = PRIORITY_TONE.map((t) => TONE_TEXT_CLASS[t]);

export function BacklogView({
  clientProjectId,
  items,
  epics,
  sprints,
}: {
  clientProjectId: string;
  items: ProjectBacklogItem[];
  epics: ProjectEpic[];
  sprints: ProjectSprint[];
}) {
  const updateItem = useUpdateBacklogItem(clientProjectId);
  const deleteItem = useDeleteBacklogItem(clientProjectId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProjectBacklogItem | null>(null);

  const backlogItems = items.filter((i) => !i.sprint_id);
  const epicById = Object.fromEntries(epics.map((e) => [e.id, e]));

  const openNew = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };
  const openEdit = (item: ProjectBacklogItem) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{backlogItems.length} itens sem sprint</p>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1.5 h-4 w-4" /> Novo item
        </Button>
      </div>

      {backlogItems.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <ListChecks className="mx-auto mb-2 h-8 w-8 opacity-40" />
          Backlog vazio.
        </div>
      ) : (
        <div className="space-y-1.5">
          {backlogItems.map((item) => {
            const epic = item.epic_id ? epicById[item.epic_id] : null;
            return (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded-lg border bg-card p-2.5"
              >
                <span className="shrink-0 text-muted-foreground">{typeIcons[item.item_type]}</span>
                <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
                {epic && (
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {epic.title}
                  </Badge>
                )}
                {item.story_points != null && (
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    {item.story_points} pts
                  </Badge>
                )}
                {item.priority > 0 && (
                  <span className={`shrink-0 text-xs font-medium ${priorityColors[item.priority]}`}>
                    ●
                  </span>
                )}
                <div className="w-40 shrink-0">
                  <Select
                    value={item.sprint_id ?? "none"}
                    onValueChange={(v) => {
                      const sprint_id = v === "none" ? null : v;
                      // Moving a backlog-status item into a sprint promotes it onto the board (A Fazer).
                      const status = sprint_id && item.status === "backlog" ? "todo" : item.status;
                      updateItem.mutate({ id: item.id, data: { sprint_id, status } });
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Mover para sprint" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Backlog</SelectItem>
                      {sprints.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(item)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => deleteItem.mutate(item.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      <BacklogItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientProjectId={clientProjectId}
        epics={epics}
        sprints={sprints}
        item={editingItem}
        defaultSprintId={null}
      />
    </div>
  );
}
